import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics } from "@/utils/analytics";
import { showNotification } from "@/redux/actions/notification";
import { requestCloseModal } from "@/redux/actions/modal";
import { LIST_SERVICE_TAG } from "@/shared/store/service/commonServiceTags";
import {
  RESOURCE_SHARE_REDUCER_PATH,
  RESOURCE_SHARE_SERVICE_TAG,
  RESOURCE_SHARE_RELATED_TAGS,
} from "@/features/LenderHub/ResourceShare/utils/resourceShareServiceTags";
import { getQueryParams } from "@/utils/httpRequest";
import { defaultLenderHubResourceShareFilters } from "@/features/LenderHub/ResourceShare/utils/defaultLenderHubResourceShareFilters";
import { setProcessingState } from "@/redux/reducers/modalConfirmation";
import { lenderHubProfileService } from "@/features/LenderHub/Profile/services/profileService";

export const resourceShareService = createApi({
  reducerPath: RESOURCE_SHARE_REDUCER_PATH,
  tagTypes: RESOURCE_SHARE_RELATED_TAGS,
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/capworks/",
    credentials: "include",
  }),
  endpoints: builder => ({
    getResourceShares: builder.query({
      query: () => `resource-shares?${getQueryParams(defaultLenderHubResourceShareFilters)}`,
      providesTags: result =>
        result
          ? [
              ...result.items.map(({ resourceShareId }) => ({
                type: RESOURCE_SHARE_SERVICE_TAG,
                id: resourceShareId,
              })),
              { type: RESOURCE_SHARE_SERVICE_TAG, id: LIST_SERVICE_TAG },
            ]
          : [{ type: RESOURCE_SHARE_SERVICE_TAG, id: LIST_SERVICE_TAG }],
    }),
    createResourceShare: builder.mutation({
      query: ({ uploadIds }) => ({
        url: "resource-shares",
        method: "POST",
        body: { uploadIds },
      }),
      invalidatesTags: RESOURCE_SHARE_RELATED_TAGS,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          data.map(resourceShare => {
            sendAnalytics(ANALYTIC_EVENTS.RESOURCE_SHARE_CREATED, resourceShare);
            return resourceShare;
          });

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Resource has been shared successfully!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(lenderHubProfileService.util.invalidateTags(["LenderHubProfile"]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    previewResourceShare: builder.mutation({
      query: ({ resourceShareId }) => ({
        url: `resource-shares/${resourceShareId}/preview`,
        method: "GET",
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          // Open preview in new tab using the URL from response
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = data.url;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          sendAnalytics(ANALYTIC_EVENTS.RESOURCE_SHARE_PREVIEWED, data.file);
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    downloadResourceShare: builder.mutation({
      query: ({ resourceShareId }) => ({
        url: `resource-shares/${resourceShareId}/physical`,
        method: "GET",
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          // Trigger download using the URL from response
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = data.url;
          a.download = data.file.fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          sendAnalytics(ANALYTIC_EVENTS.RESOURCE_SHARE_DOWNLOADED, data.file);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${data.file.fileName} has been downloaded!`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    deleteResourceShare: builder.mutation({
      query: ({ resourceShareId }) => ({
        url: `resource-shares/${resourceShareId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: RESOURCE_SHARE_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.RESOURCE_SHARE_DELETED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Resource has been deleted successfully!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );

          dispatch(requestCloseModal());
          dispatch(setProcessingState(false));
          dispatch(lenderHubProfileService.util.invalidateTags(["LenderHubProfile"]));
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),
  }),
});

export const {
  useGetResourceSharesQuery,
  useCreateResourceShareMutation,
  usePreviewResourceShareMutation,
  useDownloadResourceShareMutation,
  useDeleteResourceShareMutation,
} = resourceShareService;
